import { useState } from 'react';
import cn from 'classnames';
import { Modal } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore, MdOpenInNew, MdWarningAmber } from 'react-icons/md';
import { InteractiveDiv } from '../../../Components/InteractiveEl';
import { useRevealOnExpand } from '../../../utils/hooks/useRevealOnExpand';
// eslint-disable-next-line import-x/extensions -- bundled static JSON fixture requires the extension
import rawData from './best-practices.json';
import { CATEGORY_LABELS, TIER_LABELS } from './guidance-center-constants';
import {
  BestPracticeFinding,
  BestPracticesData,
  SCORING_CONFIG,
  VIDEO_SCORING_ENABLED
} from './scoring-engine-constants';

const data = rawData as BestPracticesData;

const APPLIES_TO_TONE: Record<BestPracticeFinding['applies_to'], string> = {
  portfolio_health: 'tone-brand',
  alexa_readiness: 'tone-warning',
  both: 'tone-neutral'
};

const APPLIES_TO_LABEL: Record<BestPracticeFinding['applies_to'], string> = {
  portfolio_health: 'Portfolio Health',
  alexa_readiness: 'Alexa Readiness',
  both: 'Both'
};

const AppliesToBadge = ({ value }: { value: BestPracticeFinding['applies_to'] }) => (
  <span className={cn('guidance-badge', APPLIES_TO_TONE[value])}>{APPLIES_TO_LABEL[value]}</span>
);

const CONFIDENCE_TONE: Record<BestPracticeFinding['confidence'], string> = {
  high: 'tone-success',
  medium: 'tone-warning',
  low: 'tone-neutral'
};

const ConfidenceBadge = ({ value }: { value: BestPracticeFinding['confidence'] }) => (
  <span className={cn('guidance-badge', CONFIDENCE_TONE[value])}>
    {value.charAt(0).toUpperCase() + value.slice(1)} Confidence
  </span>
);

const FindingCard = ({ finding }: { finding: BestPracticeFinding }) => {
  const [expanded, setExpanded] = useState(false);
  // Same disclosure problem as the two catalog tables: a card near the bottom of
  // this (scrolling) modal expanded out of sight.
  const cardRef = useRevealOnExpand<HTMLDivElement>(expanded);

  return (
    <div ref={cardRef} className='guidance-finding-card'>
      <InteractiveDiv className='guidance-finding-card-header' onClick={() => setExpanded(v => !v)}>
        <div className='guidance-finding-card-info'>
          <p>{finding.recommendation}</p>
          <div className='guidance-finding-card-badges'>
            <AppliesToBadge value={finding.applies_to} />
            <ConfidenceBadge value={finding.confidence} />
            <span className='guidance-badge tone-neutral'>
              {TIER_LABELS[finding.source_tier] ?? `Tier ${finding.source_tier}`}
            </span>
          </div>
        </div>
        {expanded ? <MdExpandLess /> : <MdExpandMore />}
      </InteractiveDiv>
      {expanded && (
        <div className='guidance-finding-card-detail'>
          <span className='guidance-finding-card-detail-label'>Why it matters</span>
          <p>{finding.rationale}</p>
          {finding.confidence === 'low' && (
            <div className='guidance-finding-card-warning'>
              <MdWarningAmber />
              <p>
                Low confidence — practitioner source only. Treat as directional until an
                Amazon-official or verified research source confirms this.
              </p>
            </div>
          )}
          <a href={finding.source_url} target='_blank' rel='noopener noreferrer'>
            {finding.source_name} <MdOpenInNew />
          </a>
        </div>
      )}
    </div>
  );
};

interface GuidanceCenterProps {
  onClose: () => void;
}

export const GuidanceCenter = ({ onClose }: GuidanceCenterProps) => {
  const c = SCORING_CONFIG;

  return (
    <Modal show centered size='xl' onHide={onClose} className='scorecard-guidance-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Scoring Weights & Best Practices</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='guidance-criteria-grid'>
          {[
            { label: `Bullets (Max ${c.bullets.weight} pts)`, text: c.bullets.criteriaText },
            { label: `Title (Max ${c.title.weight} pts)`, text: c.title.criteriaText },
            {
              label: `Description (Max ${c.description.weight} pts)`,
              text: c.description.criteriaText
            },
            { label: `Images (Max ${c.images.weight} pts)`, text: c.images.criteriaText },
            { label: `Rating (Max ${c.rating.weight} pts)`, text: c.rating.criteriaText },
            { label: `Reviews (Max ${c.reviews.weight} pts)`, text: c.reviews.criteriaText },
            // Video criterion hidden while the scraper does not supply video data
            // (VIDEO_SCORING_ENABLED). Restoring the flag brings this card back.
            ...(VIDEO_SCORING_ENABLED
              ? [{ label: `Videos (Max ${c.videos.weight} pts)`, text: c.videos.criteriaText }]
              : []),
            { label: `A+ Content (Max ${c.aplus.weight} pts)`, text: c.aplus.criteriaText }
          ].map(card => (
            <div key={card.label} className='guidance-criteria-card'>
              <span>{card.label}</span>
              <p>{card.text}</p>
            </div>
          ))}
        </div>

        {!VIDEO_SCORING_ENABLED && (
          <p className='guidance-scoring-note'>
            Note: video scoring is temporarily excluded because the data feed does not currently
            capture video assets. Scores are normalized across the remaining criteria, so listings
            are not penalized for it. Video will return automatically once that data is available.
          </p>
        )}

        {(Object.keys(data.categories) as (keyof BestPracticesData['categories'])[]).map(
          category => (
            <div key={category} className='guidance-category'>
              <h3>{CATEGORY_LABELS[category] ?? category}</h3>
              {data.categories[category].findings.map(finding => (
                <FindingCard key={finding.id} finding={finding} />
              ))}
            </div>
          )
        )}
      </Modal.Body>
    </Modal>
  );
};
